import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { isDefined } from '../../../../../../utils/variables';
import { AssetManager } from '../../../../../../viewer/asset-manager';
import { Design } from '../../../../../../viewer/elements/design';
import { SubscriptionTopic } from '../../../../../../viewer/messaging/pubsub';
import { DesignTool, DesignToolDialogState } from '../../../../../../viewer/tools/design-tool';
import { saveTool } from '../../../../../../viewer/tools/utils/context-menu/design-tool/commands/general';
import { useFeatureFlag } from '../../../../../hooks/use-feature-flag';
import { useShowAllHeights } from '../../../../../hooks/use-show-all-heights';
import { useStoredSetting } from '../../../../../hooks/use-stored-setting';
import { useSubscribe } from '../../../../../hooks/use-subscribe';
import { Button } from '../../../../button/button';
import { Icon } from '../../../../icon/icon';
import { InfoBox } from '../../../../info-box/info-box';
import { Stack } from '../../../../stack/stack';
import { SkyviewContext } from '../../../skyview-state';
import { CollapsableSectionGroup } from '../../components/collapsable-section-group';
import { FocusIcon } from '../../components/focus-icon';
import { RightPanelHeader } from '../../components/right-menu-header';
import { RightPanelContent } from '../../components/styles';
import { DesignSnappingDialog } from './design-snapping-dialog';
import { DesignToolDialogs as DesignToolDialogs } from './dialogs/design-tool-dialogs';
import { AttributesSection } from './sections/attributes-section';
import { DesignFeatureSection } from './sections/design-feature-section';
import { DesignModeSection } from './sections/design-mode-section';
import { OptionsSection } from './sections/options-section';
import { PositionSection } from './sections/position-section';
import { SnapSection } from './sections/snap-section';
import { ToolStartedPanel } from './tool-started-panel';

type Props = { tool: DesignTool };

export type SectionExpandState = {
  designMode: boolean;
  snapMode: boolean;
  placeMode: boolean;
  features: boolean;
  configureAttributes: boolean;
  options: boolean;
};

const DesignToolMenu = (props: Props) => {
  const { t } = useTranslation();
  const { selectedAsset } = React.useContext(SkyviewContext);
  const defaultAsset = useMemo<Design>(() => {
    return selectedAsset instanceof Design ? selectedAsset : props.tool.getDefaultGeometry();
  }, [selectedAsset, props.tool]);
  const [editedGeometry, setEditedGeometry] = React.useState<Design>(defaultAsset);

  const [sectionExpandState, setSectionExpandState] = React.useState<SectionExpandState>({
    designMode: true,
    snapMode: true,
    features: false,
    configureAttributes: false,
    options: false,
    placeMode: false,
  });

  // The snapping options are in a dialog, with the tooltip at its bottom, instead of in the menu.
  const [snappingDialog, setSnappingDialog] = useStoredSetting('designToolSnappingDialog');
  const [allHeights, setAllHeights] = useStoredSetting('designToolAllHeights');
  const allHeightsEnabled = useFeatureFlag('SK-7558-visualize-all-heights-option');
  useShowAllHeights(props.tool.heightsGuide, allHeightsEnabled && allHeights);
  // On until it is turned off, as the settings of what is snapped need to be seen to be trusted.
  const [tooltipSnapSettings, setTooltipSnapSettings] = useStoredSetting(
    'designToolTooltipSnapSettings',
    true,
  );

  React.useEffect(() => {
    props.tool.tooltipSnapSettings = tooltipSnapSettings;
  }, [props.tool, tooltipSnapSettings]);

  const assetFocusChangedMessage = useSubscribe(SubscriptionTopic.SkyviewAssetFocusChanged);

  const focusedAssetId = useMemo(() => {
    return assetFocusChangedMessage?.assetUuid;
  }, [assetFocusChangedMessage]);

  const [isEdited, setIsEdited] = React.useState(false);
  const [saved, setSaved] = React.useState(true);
  const [state, setState] = React.useState(DesignToolDialogState.None);

  const disableSaveIcon = !isEdited || saved;
  const isSaving = state === DesignToolDialogState.Saving;

  const initToolWithGeometry = React.useCallback(
    async (geometry: Design) => {
      props.tool.reset();
      await props.tool.onInitiate(geometry);
      setEditedGeometry(geometry);
    },
    [props.tool],
  );

  useSubscribe(SubscriptionTopic.DesignToolMenuStateChanged, (newState) => {
    setSectionExpandState({
      ...sectionExpandState,
      ...newState,
    });
  });

  React.useEffect(() => {
    props.tool.onStateChanged = () => {
      setState(props.tool.uiState);
    };

    props.tool.onEdited = () => {
      setIsEdited(true);
      props.tool.saved = false;
      setSaved(false);
    };

    props.tool.onSaved = () => {
      props.tool.saved = true;
      setSaved(true);
    };

    void initToolWithGeometry(editedGeometry);

    return () => {
      props.tool.onStateChanged = undefined;
      props.tool.onEdited = undefined;
      props.tool.onSaved = undefined;
    };
  }, [props.tool, editedGeometry, initToolWithGeometry]);

  React.useEffect(() => {
    if (!isEdited && selectedAsset instanceof Design) {
      void initToolWithGeometry(selectedAsset);
    }
  }, [props.tool, selectedAsset, isEdited, initToolWithGeometry]);

  if (!isDefined(editedGeometry) || (!isEdited && !(selectedAsset instanceof Design))) {
    return (
      <Component spacing={0}>
        <RightPanelHeader
          icon={['fal', 'pen-ruler']}
          name={t('initial.title', { ns: 'skyviewDesign' })}
        />
        <ToolStartedPanel setIsEdited={setIsEdited} />
      </Component>
    );
  }

  return (
    <Component spacing={0}>
      <RightPanelHeader icon={['fal', 'pen-ruler']} name={editedGeometry.name}>
        {isEdited && !saved && !isSaving && (
          <Icon
            color="#fac801"
            fixedWidth={true}
            icon={['fas', 'exclamation-circle']}
            title={t('initial.changesNotSaved', { ns: 'skyviewDesign' })}
          />
        )}

        <Icon
          disabled={disableSaveIcon}
          fixedWidth={true}
          icon={isSaving ? ['far', 'spinner'] : ['fal', 'cloud-upload']}
          mouseDownStyle={{ opacity: 0.5 }}
          spin={isSaving}
          title={t('save', { ns: 'common' })}
          onClick={() => saveTool(props.tool)}
          onHoverStyle={{ icon: isSaving ? ['far', 'spinner'] : ['fad', 'cloud-upload'] }}
        />

        <FocusIcon asset={editedGeometry} focusedAssetId={focusedAssetId} />

        <Icon
          disabled={!saved}
          fixedWidth={true}
          icon={['fal', 'plus']}
          mouseDownStyle={{ opacity: 0.5 }}
          title={
            saved
              ? t('initial.newIcon', { ns: 'skyviewDesign' })
              : t('initial.newIconNotSaved', { ns: 'skyviewDesign' })
          }
          onClick={async () => {
            AssetManager.instance.select(undefined);
            await initToolWithGeometry(props.tool.getDefaultGeometry());
            setIsEdited(true);
          }}
          onHoverStyle={{ icon: ['fas', 'plus'] }}
        />
      </RightPanelHeader>

      <RightPanelContent useTopPadding={!isEdited}>
        {!isEdited && (
          <InfoBox color="yellow" leftIcon={{ icon: 'info' }}>
            <Stack alignItems={'center'} direction="row" spacing={0.5}>
              <NotSavedLabel>{t('initial.editingExisting', { ns: 'skyviewDesign' })}</NotSavedLabel>
              <Button
                color="secondary"
                variant="contained"
                onClick={async () => {
                  AssetManager.instance.select(undefined);
                  await initToolWithGeometry(props.tool.getDefaultGeometry());
                  setIsEdited(true);
                }}
              >
                {t('initial.createNew', { ns: 'skyviewDesign' })}
              </Button>
            </Stack>
          </InfoBox>
        )}

        <CollapsableSectionGroup>
          <DesignModeSection expanded={sectionExpandState.designMode} tool={props.tool} />
          {!snappingDialog && (
            <>
              <SnapSection expanded={sectionExpandState.snapMode} tool={props.tool} />
              <PositionSection expanded={sectionExpandState.placeMode} tool={props.tool} />
            </>
          )}
          <DesignFeatureSection expanded={sectionExpandState.features} tool={props.tool} />
          <AttributesSection expanded={sectionExpandState.configureAttributes} tool={props.tool} />
          <OptionsSection
            allHeights={allHeights}
            expanded={sectionExpandState.options}
            snappingDialog={snappingDialog}
            tool={props.tool}
            tooltipSnapSettings={tooltipSnapSettings}
            onAllHeightsChanged={setAllHeights}
            onSnappingDialogChanged={setSnappingDialog}
            onTooltipSnapSettingsChanged={setTooltipSnapSettings}
          />
        </CollapsableSectionGroup>
      </RightPanelContent>

      {snappingDialog && (
        <DesignSnappingDialog tool={props.tool} onClose={() => setSnappingDialog(false)} />
      )}

      <DesignToolDialogs state={state} tool={props.tool} />
    </Component>
  );
};

const Component = styled(Stack)`
  height: 100%;
`;

const NotSavedLabel = styled.label`
  font-size: 0.95em;
  flex-grow: 1;
`;

export { DesignToolMenu };
